import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { getKalipHizAnalizi } from "../../../api/uretim/kalipApi";

const MACHINE_OPTIONS = [
  { value: "all", label: "Tüm Makineler" },
  { value: "1", label: "Makina 1" },
  { value: "2", label: "Makina 2" },
  { value: "3", label: "Makina 3" },
  { value: "4", label: "Makina 4" },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: "all", label: "Tüm Ürün Tipleri" },
  { value: "BORU", label: "Boru" },
  { value: "PROFİL", label: "Profil" },
];

const getIstanbulDateInput = (date = new Date()) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(date);
};

const createInitialFilters = () => {
  const today = getIstanbulDateInput();

  return {
    startDate: `${today.slice(0, 7)}-01`,
    endDate: today,
    makinaAdi: "all",
    urunTipi: "all",
  };
};

const getArrayFromResponse = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const formatNumber = (value, maximumFractionDigits = 2) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(numberValue);
};

const getMachineLabel = (machineNo) => {
  const numberValue = Number(machineNo);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return `M${numberValue}`;
};

function KalipHizAnalizi() {
  const [filters, setFilters] = useState(createInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(
    createInitialFilters
  );

  const [analysisRows, setAnalysisRows] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchAnalysis = useCallback(async (requestFilters) => {
    try {
      setLoading(true);
      setErrorMessage("");

      if (
        !requestFilters.startDate ||
        !requestFilters.endDate
      ) {
        setErrorMessage(
          "Başlangıç ve bitiş tarihini seçmelisiniz."
        );
        return;
      }

      if (
        requestFilters.startDate >
        requestFilters.endDate
      ) {
        setErrorMessage(
          "Başlangıç tarihi bitiş tarihinden sonra olamaz."
        );
        return;
      }

      const response = await getKalipHizAnalizi({
        startDate: requestFilters.startDate,
        endDate: requestFilters.endDate,
        makinaAdi: requestFilters.makinaAdi,
        urunTipi: requestFilters.urunTipi,
      });

      const records = getArrayFromResponse(response);

      setAnalysisRows(records);
      setAppliedFilters({ ...requestFilters });
      setHasSearched(true);
    } catch (error) {
      console.error(
        "Kalıp hız analizi alınırken hata oluştu:",
        error
      );

      setAnalysisRows([]);
      setHasSearched(true);

      setErrorMessage(
        error?.response?.data?.message ||
          "Kalıp hız analizi alınamadı."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initialFilters = createInitialFilters();

    fetchAnalysis(initialFilters);
  }, [fetchAnalysis]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    fetchAnalysis(filters);
  };

  const handleReset = () => {
    const initialFilters = createInitialFilters();

    setFilters(initialFilters);
    setSearchText("");
    fetchAnalysis(initialFilters);
  };

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText
      .trim()
      .toLocaleLowerCase("tr-TR");

    const rows = Array.isArray(analysisRows)
      ? [...analysisRows]
      : [];

    const searchedRows = normalizedSearch
      ? rows.filter((row) => {
          const searchableText = [
            getMachineLabel(row?.makinaAdi),
            row?.makinaAdi,
            row?.urunTipi,
            row?.kalip,
            row?.boy,
            row?.kalinlik,
            row?.ortalamaHiz,
          ]
            .filter(
              (value) =>
                value !== undefined &&
                value !== null
            )
            .join(" ")
            .toLocaleLowerCase("tr-TR");

          return searchableText.includes(
            normalizedSearch
          );
        })
      : rows;

    return searchedRows.sort((a, b) => {
      const machineDifference =
        Number(a?.makinaAdi || 0) -
        Number(b?.makinaAdi || 0);

      if (machineDifference !== 0) {
        return machineDifference;
      }

      const typeDifference = String(
        a?.urunTipi || ""
      ).localeCompare(String(b?.urunTipi || ""), "tr");

      if (typeDifference !== 0) {
        return typeDifference;
      }

      const moldDifference = String(
        a?.kalip || ""
      ).localeCompare(String(b?.kalip || ""), "tr", {
        numeric: true,
      });

      if (moldDifference !== 0) {
        return moldDifference;
      }

      const lengthDifference =
        Number(a?.boy || 0) -
        Number(b?.boy || 0);

      if (lengthDifference !== 0) {
        return lengthDifference;
      }

      return (
        Number(a?.kalinlik || 0) -
        Number(b?.kalinlik || 0)
      );
    });
  }, [analysisRows, searchText]);

  const summary = useMemo(() => {
    const rows = Array.isArray(analysisRows)
      ? analysisRows
      : [];

    const totalRecordCount = rows.reduce(
      (total, row) =>
        total + Number(row?.kayitSayisi || 0),
      0
    );

    const weightedSpeedTotal = rows.reduce(
      (total, row) => {
        const averageSpeed = Number(
          row?.ortalamaHiz || 0
        );

        const recordCount = Number(
          row?.kayitSayisi || 0
        );

        return (
          total + averageSpeed * recordCount
        );
      },
      0
    );

    const weightedAverageSpeed =
      totalRecordCount > 0
        ? weightedSpeedTotal / totalRecordCount
        : 0;

    const machineCount = new Set(
      rows
        .map((row) => Number(row?.makinaAdi))
        .filter((value) => Number.isFinite(value))
    ).size;

    const highestAverageSpeed = rows.reduce(
      (maximum, row) => {
        const speed = Number(
          row?.ortalamaHiz || 0
        );

        return speed > maximum ? speed : maximum;
      },
      0
    );

    return {
      machineCount,
      combinationCount: rows.length,
      totalRecordCount,
      weightedAverageSpeed,
      highestAverageSpeed,
    };
  }, [analysisRows]);

  const selectedMachineLabel =
    MACHINE_OPTIONS.find(
      (item) =>
        item.value ===
        String(appliedFilters.makinaAdi)
    )?.label || "Tüm Makineler";

  const selectedProductTypeLabel =
    PRODUCT_TYPE_OPTIONS.find(
      (item) =>
        item.value === appliedFilters.urunTipi
    )?.label || "Tüm Ürün Tipleri";

  return (
    <Layout>
      <style>{`
        .kalip-analysis-page {
          padding-bottom: 24px;
        }

        .kalip-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 20px;
        }

        .kalip-page-title {
          margin: 0;
          font-weight: 800;
        }

        .kalip-page-description {
          margin: 5px 0 0;
          color: var(--bs-secondary-color);
          font-size: 0.92rem;
        }

        .kalip-filter-card,
        .kalip-table-card,
        .kalip-summary-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 14px;
          background: var(--bs-body-bg);
          color: var(--bs-body-color);
        }

        .kalip-filter-card {
          margin-bottom: 18px;
        }

        .kalip-filter-label {
          font-size: 0.82rem;
          font-weight: 700;
          margin-bottom: 6px;
        }

        .kalip-summary-card {
          height: 100%;
          overflow: hidden;
          position: relative;
        }

        .kalip-summary-card::before {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 5px;
          background: var(--summary-accent);
        }

        .kalip-summary-label {
          color: var(--bs-secondary-color);
          font-size: 0.82rem;
          font-weight: 700;
          margin-bottom: 7px;
        }

        .kalip-summary-value {
          font-size: 1.65rem;
          line-height: 1;
          font-weight: 800;
          color: var(--bs-body-color);
        }

        .kalip-summary-detail {
          margin-top: 8px;
          color: var(--bs-secondary-color);
          font-size: 0.76rem;
        }

        .kalip-table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 16px;
          border-bottom: 1px solid var(--bs-border-color);
        }

        .kalip-table-title {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 800;
        }

        .kalip-table-wrapper {
          width: 100%;
          overflow-x: auto;
        }

        .kalip-analysis-table {
          min-width: 920px;
          margin-bottom: 0;
          vertical-align: middle;
        }

        .kalip-analysis-table thead th {
          padding: 13px 12px;
          background: #263445;
          color: #ffffff;
          border-color: #39495d;
          font-size: 0.82rem;
          font-weight: 700;
          white-space: nowrap;
        }

        .kalip-analysis-table tbody td {
          padding: 12px;
          font-size: 0.88rem;
          white-space: nowrap;
        }

        .kalip-analysis-table tbody tr:hover td {
          background: rgba(13, 110, 253, 0.06);
        }

        .machine-badge {
          min-width: 44px;
          font-size: 0.82rem;
        }

        .kalip-cell {
          font-size: 0.95rem;
          font-weight: 800;
        }

        .average-speed-value {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 76px;
          padding: 6px 10px;
          border-radius: 8px;
          background: rgba(25, 135, 84, 0.12);
          color: #198754;
          font-weight: 800;
        }

        .active-filter-info {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 10px;
        }

        .empty-state {
          padding: 48px 20px !important;
          text-align: center;
          color: var(--bs-secondary-color) !important;
        }

        @media (max-width: 992px) {
          .kalip-page-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .kalip-table-header {
            align-items: stretch;
            flex-direction: column;
          }

          .kalip-table-search {
            width: 100% !important;
          }
        }

        @media (max-width: 768px) {
          .kalip-page-title {
            font-size: 1.35rem;
          }

          .kalip-summary-value {
            font-size: 1.35rem;
          }

          .kalip-filter-card .card-body {
            padding: 14px;
          }

          .kalip-analysis-table thead th,
          .kalip-analysis-table tbody td {
            padding: 9px 8px;
            font-size: 0.76rem;
          }
        }
      `}</style>

      <div className="kalip-analysis-page">
        <div className="kalip-page-header">
          <div>
            <h3 className="kalip-page-title">
              Kalıp Hız Analizi
            </h3>

            <p className="kalip-page-description">
              Seçilen tarih aralığındaki kalıp, boy ve
              kalınlık kombinasyonlarının ortalama üretim
              hızlarını inceleyin.
            </p>
          </div>

          <Badge
            bg="primary"
            className="px-3 py-2"
          >
            {filteredRows.length} analiz satırı
          </Badge>
        </div>

        <Card className="kalip-filter-card shadow-sm">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row className="g-3 align-items-end">
                <Col xs={12} md={6} lg={2}>
                  <Form.Group>
                    <Form.Label className="kalip-filter-label">
                      Başlangıç Tarihi
                    </Form.Label>

                    <Form.Control
                      type="date"
                      name="startDate"
                      value={filters.startDate}
                      max={filters.endDate}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={2}>
                  <Form.Group>
                    <Form.Label className="kalip-filter-label">
                      Bitiş Tarihi
                    </Form.Label>

                    <Form.Control
                      type="date"
                      name="endDate"
                      value={filters.endDate}
                      min={filters.startDate}
                      max={getIstanbulDateInput()}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="kalip-filter-label">
                      Makine
                    </Form.Label>

                    <Form.Select
                      name="makinaAdi"
                      value={filters.makinaAdi}
                      onChange={handleFilterChange}
                    >
                      {MACHINE_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="kalip-filter-label">
                      Ürün Tipi
                    </Form.Label>

                    <Form.Select
                      name="urunTipi"
                      value={filters.urunTipi}
                      onChange={handleFilterChange}
                    >
                      {PRODUCT_TYPE_OPTIONS.map(
                        (option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} lg={2}>
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Hesaplanıyor
                        </>
                      ) : (
                        "Analiz Et"
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline-secondary"
                      disabled={loading}
                      onClick={handleReset}
                    >
                      Sıfırla
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>

            <div className="active-filter-info">
              <Badge bg="secondary">
                {appliedFilters.startDate} /{" "}
                {appliedFilters.endDate}
              </Badge>

              <Badge bg="info" text="dark">
                {selectedMachineLabel}
              </Badge>

              <Badge bg="dark">
                {selectedProductTypeLabel}
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {errorMessage && (
          <Alert variant="danger">
            {errorMessage}
          </Alert>
        )}

        <Row className="g-3 mb-4">
          <Col xs={6} xl={3}>
            <Card
              className="kalip-summary-card shadow-sm"
              style={{
                "--summary-accent": "#0d6efd",
              }}
            >
              <Card.Body className="ps-4">
                <div className="kalip-summary-label">
                  Makine Sayısı
                </div>

                <div className="kalip-summary-value">
                  {formatNumber(summary.machineCount, 0)}
                </div>

                <div className="kalip-summary-detail">
                  Analize dahil edilen makineler
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={6} xl={3}>
            <Card
              className="kalip-summary-card shadow-sm"
              style={{
                "--summary-accent": "#6f42c1",
              }}
            >
              <Card.Body className="ps-4">
                <div className="kalip-summary-label">
                  Kalıp Kombinasyonu
                </div>

                <div className="kalip-summary-value">
                  {formatNumber(
                    summary.combinationCount,
                    0
                  )}
                </div>

                <div className="kalip-summary-detail">
                  Kalıp, boy ve kalınlık satırı
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={6} xl={3}>
            <Card
              className="kalip-summary-card shadow-sm"
              style={{
                "--summary-accent": "#fd7e14",
              }}
            >
              <Card.Body className="ps-4">
                <div className="kalip-summary-label">
                  Üretim Kaydı
                </div>

                <div className="kalip-summary-value">
                  {formatNumber(
                    summary.totalRecordCount,
                    0
                  )}
                </div>

                <div className="kalip-summary-detail">
                  Ortalamaya dahil edilen kayıtlar
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={6} xl={3}>
            <Card
              className="kalip-summary-card shadow-sm"
              style={{
                "--summary-accent": "#198754",
              }}
            >
              <Card.Body className="ps-4">
                <div className="kalip-summary-label">
                  Genel Ortalama Hız
                </div>

                <div className="kalip-summary-value">
                  {formatNumber(
                    summary.weightedAverageSpeed,
                    2
                  )}
                </div>

                <div className="kalip-summary-detail">
                  m/dk · En yüksek:{" "}
                  {formatNumber(
                    summary.highestAverageSpeed,
                    2
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="kalip-table-card shadow-sm">
          <div className="kalip-table-header">
            <div>
              <h5 className="kalip-table-title">
                Kalıp Bazlı Ortalama Hızlar
              </h5>

              <small className="text-muted">
                Aynı makine, kalıp, boy ve kalınlıktaki
                üretim hızlarının ortalamasıdır.
              </small>
            </div>

            <InputGroup
              className="kalip-table-search"
              style={{ width: "320px" }}
            >
              <InputGroup.Text>Ara</InputGroup.Text>

              <Form.Control
                type="search"
                placeholder="Kalıp, makine, boy..."
                value={searchText}
                onChange={(event) =>
                  setSearchText(event.target.value)
                }
              />
            </InputGroup>
          </div>

          <div className="kalip-table-wrapper">
            <Table
              bordered
              hover
              responsive={false}
              className="kalip-analysis-table"
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Makine</th>
                  <th>Ürün Tipi</th>
                  <th>Kalıp</th>
                  <th className="text-end">
                    Boy (mm)
                  </th>
                  <th className="text-end">
                    Kalınlık (mm)
                  </th>
                  <th className="text-center">
                    Ortalama Hız
                  </th>
                  <th className="text-end">
                    Kayıt Sayısı
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading &&
                analysisRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="empty-state"
                    >
                      <Spinner
                        animation="border"
                        className="mb-3"
                      />

                      <div>
                        Kalıp hızları hesaplanıyor...
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, index) => (
                    <tr
                      key={[
                        row?.makinaAdi,
                        row?.urunTipi,
                        row?.kalip,
                        row?.boy,
                        row?.kalinlik,
                        index,
                      ].join("-")}
                    >
                      <td className="text-muted">
                        {index + 1}
                      </td>

                      <td>
                        <Badge
                          bg="primary"
                          className="machine-badge"
                        >
                          {getMachineLabel(
                            row?.makinaAdi
                          )}
                        </Badge>
                      </td>

                      <td>
                        <Badge
                          bg={
                            row?.urunTipi === "PROFİL"
                              ? "info"
                              : "secondary"
                          }
                          text={
                            row?.urunTipi === "PROFİL"
                              ? "dark"
                              : undefined
                          }
                        >
                          {row?.urunTipi || "-"}
                        </Badge>
                      </td>

                      <td className="kalip-cell">
                        {row?.kalip || "-"}
                      </td>

                      <td className="text-end">
                        {formatNumber(row?.boy, 2)}
                      </td>

                      <td className="text-end fw-semibold">
                        {formatNumber(
                          row?.kalinlik,
                          2
                        )}
                      </td>

                      <td className="text-center">
                        <span className="average-speed-value">
                          {formatNumber(
                            row?.ortalamaHiz,
                            2
                          )}
                        </span>

                        <span className="ms-2 text-muted">
                          m/dk
                        </span>
                      </td>

                      <td className="text-end fw-bold">
                        {formatNumber(
                          row?.kayitSayisi,
                          0
                        )}
                      </td>
                    </tr>
                  ))
                )}

                {!loading &&
                  hasSearched &&
                  filteredRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="empty-state"
                      >
                        Seçilen filtrelere uygun kalıp
                        kaydı bulunamadı.
                      </td>
                    </tr>
                  )}
              </tbody>
            </Table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default KalipHizAnalizi;